import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Heart, X, MapPin, ChevronUp, ChevronDown, Sparkles, Home, Building, TreePine, Wallet, Filter, SlidersHorizontal, Eye, Bed, Bath, Square, DollarSign } from "lucide-react";
import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { VibeTag, DetailedVibeTagsSection, getAllVibeTagsArray } from "@/components/VibeTag";
import { PropertyDetailModal } from "@/components/PropertyDetailModal";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

import { MEXICAN_CITIES as FILTER_CITIES } from "@/constants/mexicanCities";

// Category icons and colors
const CATEGORY_CONFIG = {
  emotional: { 
    icon: Heart, 
    color: "text-rose-500", 
    bgColor: "bg-rose-50",
    progressColor: "bg-rose-500"
  },
  technical: { 
    icon: Building, 
    color: "text-blue-500", 
    bgColor: "bg-blue-50",
    progressColor: "bg-blue-500"
  },
  urban: { 
    icon: TreePine, 
    color: "text-green-500", 
    bgColor: "bg-green-50",
    progressColor: "bg-green-500"
  },
  financial: { 
    icon: Wallet, 
    color: "text-amber-500", 
    bgColor: "bg-amber-50",
    progressColor: "bg-amber-500"
  }
};

// Match Score Breakdown Component
const MatchBreakdown = ({ breakdown }) => {
  if (!breakdown) return null;

  const categories = ['emotional', 'technical', 'urban', 'financial'];

  return (
    <div className="space-y-3" data-testid="match-breakdown">
      <h4 className="text-sm font-semibold text-[#384451] flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-primary" />
        Desglose de Compatibilidad
      </h4>
      <div className="grid grid-cols-2 gap-3">
        {categories.map(cat => {
          const data = breakdown[cat];
          if (!data) return null;
          
          const config = CATEGORY_CONFIG[cat];
          const Icon = config.icon;
          
          return (
            <div 
              key={cat} 
              className={`p-3 rounded-lg ${config.bgColor}`}
              data-testid={`breakdown-${cat}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${config.color}`} />
                <span className="text-xs font-medium text-[#384451]">{data.label}</span>
                <span className="text-xs text-[#384451]/60 ml-auto">{data.weight}%</span>
              </div>
              <div className="relative h-2 bg-white/50 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${data.score}%` }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className={`absolute h-full rounded-full ${config.progressColor}`}
                />
              </div>
              <p className="text-right text-xs font-bold mt-1 text-[#384451]">{data.score}%</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Función para obtener tags destacados (matched con el perfil)
const getHighlightedTags = (propertyTags, userProfile) => {
  if (!propertyTags || !userProfile) return [];
  
  const allTags = getAllVibeTagsArray();
  const matchedTags = [];
  
  propertyTags.forEach(tagId => {
    const tag = allTags.find(t => t.id === tagId);
    if (tag && isTagMatched(tag, userProfile)) {
      matchedTags.push(tag);
    }
  });
  
  return matchedTags.slice(0, 3);
};

// Verificar si un tag coincide con el perfil del usuario
const isTagMatched = (tag, userProfile) => {
  if (!userProfile) return false;
  
  return tag.questions.some(questionId => {
    const answer = userProfile[questionId];
    if (answer === undefined || answer === null || answer === 'skipped') return false;
    if (typeof answer === 'boolean') return answer === true;
    if (typeof answer === 'number') return answer >= 7;
    return answer !== null;
  });
};

const SwipeCard = ({ property, onSwipe, userProfile, onViewDetails }) => {
  const [showDetails, setShowDetails] = useState(false);
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-25, 25]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0, 1, 1, 1, 0]);

  const handleDragEnd = (event, info) => {
    if (info.offset.x > 100) {
      onSwipe("like");
    } else if (info.offset.x < -100) {
      onSwipe("pass");
    }
  };

  const highlightedTags = getHighlightedTags(property.vibe_tags, userProfile);

  return (
    <motion.div
      className="absolute inset-0 w-full h-full bg-white rounded-2xl minimal-shadow overflow-hidden cursor-grab active:cursor-grabbing swipe-card"
      style={{ x, rotate, opacity }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={handleDragEnd}
      data-testid="swipe-card"
    >
      <div className="relative h-full flex flex-col">
        {/* Imagen principal */}
        <div className={`relative ${showDetails ? 'h-2/5' : 'h-full'} transition-all duration-300`}>
          <img src={property.images?.[0] || '/placeholder-property.jpg'} alt={property.title} className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
          
          {/* Badge de compatibilidad */}
          <div className="absolute top-4 right-4 bg-primary text-[#384451] px-4 py-2 rounded-full font-semibold text-sm flex items-center gap-2" data-testid="match-score-badge">
            <Sparkles className="w-4 h-4" />
            {property.match_score}% Match
          </div>
          
          {/* City match indicator */}
          {property.city_match && (
            <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              En tu zona
            </div>
          )}
          
          {/* Vibe Tags destacados sobre la imagen */}
          {highlightedTags.length > 0 && !property.city_match && (
            <div className="absolute top-4 left-4 flex flex-wrap gap-2" data-testid="highlight-tags">
              {highlightedTags.map(tag => (
                <VibeTag key={tag.id} tag={tag} matched={true} size="sm" />
              ))}
            </div>
          )}
          
          {/* Información básica */}
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <h2 className="text-2xl font-semibold mb-2">{property.title}</h2>
            <p className="text-base mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {property.municipality}
            </p>
            {!showDetails && (
              <p className="text-sm opacity-90 line-clamp-2">{property.story}</p>
            )}
          </div>
        </div>
        
        {/* Panel de detalles expandible */}
        <AnimatePresence>
          {showDetails && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: '60%' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white p-4 overflow-y-auto"
            >
              <p className="text-[#384451]/80 text-sm mb-4">{property.story}</p>
              
              {/* Match Breakdown */}
              {property.match_breakdown && (
                <MatchBreakdown breakdown={property.match_breakdown} />
              )}
              
              {/* Sección de Vibe Tags detallados */}
              {property.vibe_tags && property.vibe_tags.length > 0 && (
                <div className="mt-4">
                  <DetailedVibeTagsSection 
                    propertyTags={property.vibe_tags} 
                    userProfile={userProfile}
                  />
                </div>
              )}
              
              {/* Características adicionales */}
              {property.features && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h4 className="text-sm font-semibold text-[#384451] mb-2">Detalles</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-[#384451]/70">
                    {property.features.bedrooms && (
                      <span className="flex items-center gap-1"><Bed className="w-4 h-4" /> {property.features.bedrooms} recámaras</span>
                    )}
                    {property.features.bathrooms && (
                      <span className="flex items-center gap-1"><Bath className="w-4 h-4" /> {property.features.bathrooms} baños</span>
                    )}
                    {(property.features.land_area_m2 || property.features.area) && (
                      <span className="flex items-center gap-1"><Square className="w-4 h-4" /> {property.features.land_area_m2 || property.features.area} m² terreno</span>
                    )}
                    {property.features.construction_area_m2 && (
                      <span className="flex items-center gap-1"><Home className="w-4 h-4" /> {property.features.construction_area_m2} m² construido</span>
                    )}
                    {property.features.price && (
                      <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" /> ${(property.features.price / 1000000).toFixed(1)}M MXN</span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Ver Ficha Completa Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDetails && onViewDetails(property);
                }}
                className="mt-4 w-full bg-primary hover:bg-[#b8c100] text-[#384451] py-2 px-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
                data-testid="view-full-details-btn"
              >
                <Eye className="w-4 h-4" />
                Ver Ficha Completa
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Botón para expandir/colapsar detalles */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowDetails(!showDetails);
          }}
          className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-white/90 hover:bg-white text-[#384451] px-4 py-2 rounded-full shadow-lg flex items-center gap-1 text-sm font-medium transition-all"
          data-testid="toggle-details-btn"
        >
          {showDetails ? (
            <>
              <ChevronDown className="w-4 h-4" />
              Menos detalles
            </>
          ) : (
            <>
              <ChevronUp className="w-4 h-4" />
              Ver Match Score
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
};

// Match Celebration Modal
const MatchCelebration = ({ matchData, property, onClose }) => {
  if (!matchData) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.5, opacity: 0 }}
        className="bg-white rounded-2xl p-6 max-w-md w-full text-center"
        onClick={e => e.stopPropagation()}
        data-testid="match-celebration"
      >
        <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Heart className="w-10 h-10 text-primary fill-primary" />
        </div>
        <h2 className="text-2xl font-bold text-[#384451] mb-2">¡Es una Conexión!</h2>
        <p className="text-3xl font-bold text-primary mb-4">{matchData.match_score}% Compatible</p>
        
        {property && (
          <p className="text-[#384451]/70 mb-4">
            Hiciste match con <strong>{property.title}</strong>
          </p>
        )}
        
        {matchData.match_breakdown && (
          <div className="mb-6">
            <MatchBreakdown breakdown={matchData.match_breakdown} />
          </div>
        )}
        
        <p className="text-sm text-[#384451]/60 mb-4">
          Un agente se pondrá en contacto contigo para los siguientes pasos
        </p>
        
        <Button 
          onClick={onClose}
          className="w-full bg-primary text-[#384451] hover:bg-[#b8c100]"
        >
          Continuar Explorando
        </Button>
      </motion.div>
    </motion.div>
  );
};

export const SwipePage = () => {
  const [currentProperty, setCurrentProperty] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matchData, setMatchData] = useState(null);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filterCity, setFilterCity] = useState("");
  const [priceRange, setPriceRange] = useState([0, 50]); // In millions MXN
  const [filtersApplied, setFiltersApplied] = useState(false);
  
  // Property detail modal state
  const [showPropertyDetailModal, setShowPropertyDetailModal] = useState(false);
  const [selectedPropertyDetail, setSelectedPropertyDetail] = useState(null);

  useEffect(() => {
    fetchUserProfile();
    fetchNextProperty();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/onboarding/profile`);
      // Flatten the profile for easier tag matching
      const flatProfile = {
        ...response.data.emotional,
        ...response.data.technical,
        ...response.data.urban,
        ...response.data.financial
      };
      setUserProfile(flatProfile);
    } catch (error) {
      console.error("Failed to fetch user profile", error);
    }
  };

  const fetchNextProperty = async (city = null, minPrice = null, maxPrice = null) => {
    setLoading(true);
    try {
      const params = {};
      if (city) params.city = city;
      if (minPrice !== null) params.min_price = minPrice * 1000000; // Convert to actual price
      if (maxPrice !== null && maxPrice < 50) params.max_price = maxPrice * 1000000;
      
      const response = await axios.get(`${API}/swipe/next`, { params });
      setCurrentProperty(response.data);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Por favor completa tu perfil primero");
      } else if (error.response?.status === 404) {
        setCurrentProperty(null);
      } else {
        console.error("Failed to fetch property", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    setFiltersApplied(filterCity !== "" || priceRange[0] > 0 || priceRange[1] < 50);
    fetchNextProperty(
      filterCity || null,
      priceRange[0] > 0 ? priceRange[0] : null,
      priceRange[1] < 50 ? priceRange[1] : null
    );
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilterCity("");
    setPriceRange([0, 50]);
    setFiltersApplied(false);
    fetchNextProperty();
    setShowFilters(false);
  };

  // View property full details
  const viewPropertyDetails = (property) => {
    setSelectedPropertyDetail({
      property: property,
      matchScore: property.match_score,
      matchBreakdown: property.match_breakdown
    });
    setShowPropertyDetailModal(true);
  };

  const handleSwipe = async (action) => {
    if (!currentProperty) return;

    try {
      const response = await axios.post(`${API}/swipe`, {
        property_id: currentProperty.id,
        action
      });

      if (response.data.matched) {
        setMatchData(response.data);
        toast.success(`¡Es una conexión! ${response.data.match_score}% de compatibilidad! 🎉`);
      }

      // Small delay before fetching next property for better UX
      setTimeout(() => {
        // Use filters if applied
        if (filtersApplied) {
          fetchNextProperty(
            filterCity || null,
            priceRange[0] > 0 ? priceRange[0] : null,
            priceRange[1] < 50 ? priceRange[1] : null
          );
        } else {
          fetchNextProperty();
        }
      }, response.data.matched ? 100 : 300);
      
    } catch (error) {
      toast.error("Error al registrar la acción");
    }
  };

  const handleCloseMatch = () => {
    setMatchData(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-md mx-auto py-8">
        <h1 className="text-3xl font-semibold text-[#384451] mb-2 text-center" data-testid="swipe-page-title">
          Encuentra tu Hogar Perfecto
        </h1>
        <p className="text-center text-[#384451]/70 mb-4">Desliza para descubrir propiedades compatibles</p>

        {/* Filter Button */}
        <div className="flex justify-center mb-6">
          <Button
            variant={filtersApplied ? "default" : "outline"}
            onClick={() => setShowFilters(!showFilters)}
            className={`gap-2 ${filtersApplied ? "bg-primary text-[#384451]" : ""}`}
            data-testid="toggle-filters-btn"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filtros {filtersApplied && "(activos)"}
          </Button>
        </div>

        {/* Filters Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <Card className="mb-6 p-4">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[#384451] mb-2 block">
                      Ciudad
                    </label>
                    <Select value={filterCity || "_all"} onValueChange={(v) => setFilterCity(v === "_all" ? "" : v)}>
                      <SelectTrigger data-testid="filter-city-select">
                        <SelectValue placeholder="Todas las ciudades" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">Todas las ciudades</SelectItem>
                        {FILTER_CITIES.map(city => (
                          <SelectItem key={city} value={city}>{city}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#384451] mb-2 block">
                      Rango de Precio (MXN)
                    </label>
                    <div className="px-2">
                      <Slider
                        value={priceRange}
                        onValueChange={setPriceRange}
                        min={0}
                        max={50}
                        step={1}
                        className="my-4"
                        data-testid="price-range-slider"
                      />
                      <div className="flex justify-between text-sm text-[#384451]/70">
                        <span>${priceRange[0]}M</span>
                        <span>{priceRange[1] >= 50 ? "$50M+" : `$${priceRange[1]}M`}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={clearFilters}
                      data-testid="clear-filters-btn"
                    >
                      Limpiar
                    </Button>
                    <Button
                      className="flex-1 bg-primary text-[#384451]"
                      onClick={applyFilters}
                      data-testid="apply-filters-btn"
                    >
                      Aplicar Filtros
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Active Filters Indicator */}
        {filtersApplied && !showFilters && (
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {filterCity && (
              <span className="text-xs bg-primary/20 text-[#384451] px-3 py-1 rounded-full">
                📍 {filterCity}
              </span>
            )}
            {(priceRange[0] > 0 || priceRange[1] < 50) && (
              <span className="text-xs bg-primary/20 text-[#384451] px-3 py-1 rounded-full">
                💰 ${priceRange[0]}M - {priceRange[1] >= 50 ? "$50M+" : `$${priceRange[1]}M`}
              </span>
            )}
          </div>
        )}

        <div className="relative h-[600px] perspective-1000">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-[#384451]/70">Buscando tu match perfecto...</p>
              </div>
            </div>
          ) : currentProperty ? (
            <SwipeCard 
              property={currentProperty} 
              onSwipe={handleSwipe}
              userProfile={userProfile}
              onViewDetails={viewPropertyDetails}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <Card className="modern-card border-0 p-8 text-center">
                <CardTitle className="mb-4 text-[#384451]">No Hay Más Propiedades</CardTitle>
                <p className="text-[#384451]/70 mb-4">¡Vuelve más tarde para nuevas publicaciones!</p>
                <Button 
                  onClick={fetchNextProperty}
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/10"
                >
                  Buscar de nuevo
                </Button>
              </Card>
            </div>
          )}
        </div>

        {currentProperty && (
          <div className="flex justify-center gap-6 mt-8">
            <Button
              size="lg"
              variant="outline"
              className="w-16 h-16 rounded-full border-2 border-gray-200 hover:bg-gray-50 hover:border-red-500 transition-all"
              onClick={() => handleSwipe("pass")}
              data-testid="pass-btn"
            >
              <X className="w-8 h-8 text-red-500" />
            </Button>
            <Button
              size="lg"
              className="w-16 h-16 rounded-full bg-primary hover:bg-[#b8c100] text-[#384451] transition-all hover:scale-110"
              onClick={() => handleSwipe("like")}
              data-testid="like-btn"
            >
              <Heart className="w-8 h-8" />
            </Button>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-6 text-center text-sm text-[#384451]/50">
          <p>Desliza ← para pasar • Desliza → para dar like</p>
        </div>
      </div>

      {/* Match Celebration Modal */}
      <AnimatePresence>
        {matchData && (
          <MatchCelebration 
            matchData={matchData} 
            property={currentProperty}
            onClose={handleCloseMatch}
          />
        )}
      </AnimatePresence>

      {/* Property Detail Modal for full property view */}
      <PropertyDetailModal
        open={showPropertyDetailModal}
        onOpenChange={setShowPropertyDetailModal}
        property={selectedPropertyDetail?.property}
        showMatchScore={true}
        matchScore={selectedPropertyDetail?.matchScore}
        matchBreakdown={selectedPropertyDetail?.matchBreakdown}
      />
    </div>
  );
};

export default SwipePage;
